import { FunnelVO } from './../interfaces/vo/PlanForecastBluePrintVO';
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpResponse, HttpParams } from '@angular/common/http';
import { PlannerEndpoints } from '../constants/planner-endpoints.const';
import { ActionCommand } from 'src/app/core/enums/action-commands.enum';
import { PlanConfigVO } from '../interfaces/vo/PlanConfigVO';
import { IdNameVO } from '../interfaces/vo/IdNameVO';
import { PlanForecastBluePrintVO } from '../interfaces/vo/PlanForecastBluePrintVO';
import { FunnelNameActionMap } from '../constants/funnelNameActionMap';

export enum HTTPMethod {
    GET = 'GET',
    PUT = 'PUT',
    POST = 'POST'
}

@Injectable()
export class DataService {

    constructor(private http: HttpClient) { }

    public currentInventorySources(marketId) {
        let param = new HttpParams();
        param = param.append('marketId', marketId);
        param = param.append('mediaChannelId', '2');
        return this.http.get(PlannerEndpoints.dspRtbExchanges, {
            params: param
        }).toPromise();
    }

    public getMarketSiteAppList(marketId, inventoryListType, targetingType, limit?, offset?, searchString?) {
        let param = new HttpParams();
        param = param.append('marketId', marketId);
        param = param.append('inventoryListType', inventoryListType);
        if (limit && limit > 0) {
            param = param.append('limit', limit);
        } else {
            param = param.append('limit', '-1');
        }
        if (offset && offset >= 0) {
            param = param.append('offset', offset);
        } else {
            param = param.append('offset', '0');
        }
        if (searchString) {
            param = param.append('searchString', searchString);
        }
        //TODO: update in backend to filter blacklist/whitelist
        //param = param.append('targetingType', targetingType);
        return this.http.get(PlannerEndpoints.marketSiteList, {
            params: param
        }).toPromise().then((response: any) => response.results);
    }

    public getPlanResult(planId: string) {
        return this.http.get(PlannerEndpoints.planResult + planId).toPromise();
    };

    public getAvailableTargetDeals(marketId, advertiserId, mediaChannelId, dealTypeIds, isAmobeePreferred) {
        let param = new HttpParams();
        param = param.append('marketId', marketId);
        param = param.append('advertiserId', advertiserId);
        param = param.append('mediaChannelId', mediaChannelId);
        if (Array.isArray(dealTypeIds)) {
            dealTypeIds.forEach(id => {
                param = param.append('dealTypeIds', id);
            });
        } else {
            param = param.append('dealTypeIds', dealTypeIds);
        }
        param = param.append('isAmobeePreferred', isAmobeePreferred);
        return this.http.get(PlannerEndpoints.dspDeals, {
            params: param
        }).toPromise().then((data: any) => {
            return data.results;
        });
    }

    public getDealExchanges(marketId) {
        let param = new HttpParams();
        param = param.append('marketId', marketId);
        param = param.append('mediaChannelId', '2');
        return this.http.get(PlannerEndpoints.dspDealExchanges, {
            params: param
        }).toPromise();
    }

    public async processRequest(
        type: string,
        url: string,
        body?: any,
        allowReject?: boolean
    ): Promise<any> {
        const config: any = {
            body: body,
            headers: new HttpHeaders({
                Accept: 'application/json, text/plain, */*'
            })
        };

        return await this.http
            .request<HttpResponse<Object>>(type, url, config)
            .toPromise()
            .then((res: any) => {
                return Promise.resolve(res);
            })
            .catch(err => {
                if (allowReject) {
                    return Promise.reject(err);
                }
                return Promise.resolve(err);
            });
    }

    public savePlan(planConfigVO: PlanConfigVO) {
        return this.http.post(PlannerEndpoints.planCRUD, {
            operation: planConfigVO.id ? ActionCommand.Update : ActionCommand.Create,
            cid: null,
            vo: planConfigVO
        }).toPromise()
            .then((response: PlanConfigVO) => {
                console.log(response);
                return response;
            })
            .catch(this.handleErrorPromise);
    }

    public getPlan(plannerId) {
        return this.http.post(PlannerEndpoints.planCRUD, {
            operation: ActionCommand.Read,
            cid: null,
            vo: { id: plannerId }
        }).toPromise()
            .then((response: PlanConfigVO) => {
                console.log(response);
                return response;
            })
            .catch(this.handleErrorPromise);
    }

    public getPlanDetails(plannerId) {
        return this.http.get(PlannerEndpoints.planDetail + plannerId).toPromise()
            .then((response: PlanConfigVO) => {
                console.log(response);
                return response;
            })
            .catch(this.handleErrorPromise);
    }

    public getPlanDefaults(params) {
        return this.http.get(PlannerEndpoints.planDefault, { params: params }).toPromise()
            .then((response: PlanConfigVO) => {
                return response;
            })
            .catch(this.handleErrorPromise);
    }

    public getTpaPlatforms() {
        return this.http.get(PlannerEndpoints.tpaPlatforms).toPromise()
            .then((response: Array<IdNameVO>) => {
                return response;
            })
            .catch(this.handleErrorPromise);
    }

    public getPlanForecastBluePrint(plannerId): Promise<FunnelVO | []> {
        return this.http.get(PlannerEndpoints.planForecastBlueprint + plannerId).toPromise()
            .then((response: PlanForecastBluePrintVO) => {
                return this.convertForecastBluePrintDataToFunnelVO(response);
            })
            .catch(() => Promise.resolve([]));
    }

    private convertForecastBluePrintDataToFunnelVO(data: PlanForecastBluePrintVO): FunnelVO {
        let vo: FunnelVO = {
            exclusionData: [{ todayExclusion: this.prepareDataForFunnelWidget(data.exclusionMetrics) }],
            lastUpdatedTimeStamp: new Date(data.lastModifiedDate),
            title: "Funnel",
            isSidePanelView: false,
            funnelView: true
        };

        return vo;
    }

    private prepareDataForFunnelWidget(data) {
        let totalImpression = data[0].denominator;
        let mappedData = data.map(x => ({
            severity: parseFloat((x.severity * 100).toFixed(2)),
            numExclusions: x.numExclusions,
            denominator: x.denominator === 0 ? 0 : x.denominator,
            exclusionPercentage: (x.numExclusions * 100 / x.denominator).toFixed(2),
            inclusionPercentage: x.denominator === 0 ? 0 : (100 - x.numExclusions * 100 / x.denominator).toFixed(2),
            relativeSeverity: x.denominator === 0 ? 0 : (x.numExclusions * 100 / x.denominator).toFixed(2),
            absoluteSeverity: totalImpression === 0 ? 0 : parseFloat((x.numExclusions * 100 / totalImpression).toFixed(2)),
            action: FunnelNameActionMap[x.name].action,
            reason: FunnelNameActionMap[x.name].reason,
            displayName: FunnelNameActionMap[x.name].name,
            tceId: x.tceId,
            name: x.name,
            exclusionSubCatOrderIndex: x.exclusionSubCatOrderIndex,
            exclusionSubCategory: x.exclusionSubCategory,
            category: x.category,
            totalImpression: totalImpression,
            targetingDisplayName: "N/A"
        }));
        return mappedData;
    }

    private handleErrorPromise(error: Response | any) {
        console.error(error.message || error);
        return Promise.reject(error);
    }
}
